import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { FileText, Plus, Edit, Trash2, Image as ImageIcon, Sparkles } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { API } from './constants';
import { AssetPickerDialog } from './AssetPickerDialog';

const EMPTY_FORM = {
  title: '',
  excerpt: '',
  content: '',
  image_url: '',
  category: '',
  published: false,
};

export const BlogTab = ({ blogPosts, showModal, setShowModal, editingItem, setEditingItem, fetchAllData }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const handleSeedCalendar = async () => {
    const confirmed = window.confirm(
      'Seed the SEO content calendar?\n\n' +
      'This will insert:\n' +
      '  • 1 published pillar post ("What Is a Strategic Event Concierge?")\n' +
      '  • 6 hidden drafts (you publish each one on your own schedule)\n\n' +
      'Already-existing posts are skipped. Safe to run more than once.'
    );
    if (!confirmed) return;
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/admin/seed-content-calendar`, {}, { withCredentials: true });
      const { inserted_count, skipped_count } = res.data;
      toast.success(`Seeded ${inserted_count} post${inserted_count === 1 ? '' : 's'} (${skipped_count} skipped)`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed content calendar');
    } finally {
      setSeeding(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/blog/${editingItem.post_id}`, formData, { withCredentials: true });
        toast.success('Post updated');
      } else {
        await axios.post(`${API}/blog`, formData, { withCredentials: true });
        toast.success('Post created');
      }

      setShowModal(false);
      setEditingItem(null);
      setFormData(EMPTY_FORM);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save post');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await axios.delete(`${API}/blog/${postId}`, { withCredentials: true });
      toast.success('Post deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const openEditModal = (post) => {
    setEditingItem(post);
    setFormData({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      image_url: post.image_url || '',
      category: post.category,
      published: post.published,
    });
    setShowModal(true);
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-heading text-2xl text-white">Blog Posts</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={handleSeedCalendar}
            disabled={seeding}
            className="flex items-center gap-2 px-6 py-3 border border-[#D4AF37] text-[#D4AF37] text-sm uppercase tracking-widest hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="seed-content-calendar-btn"
            title="Insert pillar post + 6 SEO-briefed drafts (idempotent)"
          >
            <Sparkles size={16} /> {seeding ? 'Seeding…' : 'Seed Content Calendar'}
          </button>
          <button
            onClick={() => {
              setEditingItem(null);
              setFormData(EMPTY_FORM);
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-6 py-3 bg-[#1B1B1B] text-white text-sm uppercase tracking-widest hover:bg-[#111111] transition-colors"
            data-testid="add-post-btn"
          >
            <Plus size={16} /> Add Post
          </button>
        </div>
      </div>

      {blogPosts.length === 0 ? (
        <div className="bg-[#0a0a0a] border border-white/10 p-12 text-center">
          <FileText className="w-12 h-12 text-[#B8AEA1] mx-auto mb-4" />
          <p className="text-white/60">No blog posts yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {blogPosts.map((post) => (
            <div
              key={post.post_id}
              className="bg-[#0a0a0a] border border-white/10 p-6 flex items-center justify-between"
              data-testid={`post-${post.post_id}`}
            >
              <div className="flex items-center gap-4">
                {post.image_url && (
                  <img src={post.image_url} alt={post.title} className="w-16 h-16 object-cover" />
                )}
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-white font-medium">{post.title}</h3>
                    {post.published ? (
                      <span className="text-xs bg-green-50 text-green-600 px-2 py-1">Published</span>
                    ) : (
                      <span className="text-xs bg-[#B8AEA1]/20 text-white/60 px-2 py-1">Draft</span>
                    )}
                  </div>
                  <p className="text-white/60 text-sm">{post.category}</p>
                  <p className="text-white/40 text-xs mt-1">
                    {format(new Date(post.created_at), 'MMM d, yyyy')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEditModal(post)}
                  className="p-2 text-white/40 hover:text-white"
                  title="Edit"
                >
                  <Edit size={18} />
                </button>
                <button
                  onClick={() => handleDelete(post.post_id)}
                  className="p-2 text-white/40 hover:text-red-500"
                  title="Delete"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Post Modal */}
      <Dialog open={showModal} onOpenChange={(open) => {
        setShowModal(open);
        if (!open) setEditingItem(null);
      }}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-white">
              {editingItem ? 'Edit Post' : 'Add New Post'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Title</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors"
                placeholder="Post title"
              />
            </div>
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Category</label>
              <input
                type="text"
                required
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors"
                placeholder="e.g., Design Trends, Planning Tips"
              />
            </div>
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Image URL</label>
              <div className="flex items-center gap-2">
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="flex-1 bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors"
                  placeholder="https://... or pick from library"
                  data-testid="blog-image-url-input"
                />
                <button
                  type="button"
                  onClick={() => setShowAssetPicker(true)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-[#1B1B1B] text-white text-xs uppercase tracking-widest hover:bg-[#111111] transition-colors border border-white/10 whitespace-nowrap"
                  data-testid="blog-pick-asset-btn"
                >
                  <ImageIcon className="w-3.5 h-3.5" />
                  From Library
                </button>
              </div>
              {formData.image_url && (
                <img
                  src={formData.image_url}
                  alt="Preview"
                  className="mt-3 max-h-40 object-contain border border-white/10"
                  onError={(e) => { e.target.style.display = 'none'; }}
                  onLoad={(e) => { e.target.style.display = 'block'; }}
                />
              )}
            </div>
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Excerpt</label>
              <textarea
                required
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                className="w-full bg-transparent border border-white/20 p-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors min-h-[80px] resize-none"
                placeholder="Brief summary for blog listing..."
              />
            </div>
            <div>
              <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Content</label>
              <div className="rich-editor-wrapper" data-testid="blog-content-editor">
                <ReactQuill
                  theme="snow"
                  value={formData.content}
                  onChange={(val) => setFormData({ ...formData, content: val })}
                  modules={{
                    toolbar: [
                      [{ header: [2, 3, false] }],
                      ['bold', 'italic', 'underline', 'blockquote'],
                      [{ list: 'ordered' }, { list: 'bullet' }],
                      ['link'],
                      ['clean'],
                    ],
                  }}
                  formats={['header', 'bold', 'italic', 'underline', 'blockquote', 'list', 'link']}
                  placeholder="Write your article..."
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, published: !formData.published })}
                className={`w-12 h-6 rounded-full transition-colors ${
                  formData.published ? 'bg-[#D4AF37]' : 'bg-[#B8AEA1]/30'
                }`}
              >
                <div className={`w-5 h-5 rounded-full bg-[#0a0a0a] transition-transform ${
                  formData.published ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
              <span className="text-white/60 text-sm">
                {formData.published ? 'Published' : 'Draft'}
              </span>
            </div>
            <button type="submit" className="w-full py-4 bg-[#1B1B1B] text-white text-sm uppercase tracking-widest hover:bg-[#111111] transition-colors">
              {editingItem ? 'Update Post' : 'Create Post'}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <AssetPickerDialog
        open={showAssetPicker}
        onClose={() => setShowAssetPicker(false)}
        onSelect={(url) => {
          setFormData({ ...formData, image_url: url });
          setShowAssetPicker(false);
        }}
      />
    </div>
  );
};
